import type { DefectFrequency, VehicleDefectProfile } from '../api/types'
import { formatLongDate, vehicleName } from './format'

/**
 * The defect profile's heading.
 *
 * Phrased as a question about the *model* rather than "APK-bevindingen", which would read as this
 * car's own findings — those are in the inspection card above and are a different fact entirely.
 */
export const DEFECT_PROFILE_TITLE = 'Wat gaat er bij dit model vaak mis?'

/** How many entries are worth showing before the tail becomes noise. */
const DEFAULT_LIMIT = 6

/**
 * Whether there is a profile worth rendering.
 *
 * A 200 carries no figures more often than not — no type approval, too few siblings, a model too new
 * to have been inspected — and a cohort can also have inspections but no recorded defect at all,
 * which is a real and rather flattering answer that nonetheless leaves nothing to draw.
 *
 * `thisVehicle` counts on its own. It is the one part of the response about the car in the report,
 * and "deze auto: 0,4 per keuring, dit model: 0,0" is a complete and rather pointed answer even
 * with both ranked lists empty.
 */
export function hasDefectProfile(profile: VehicleDefectProfile | undefined): profile is VehicleDefectProfile {
  if (!profile || profile.unavailable || !profile.inspectionsCovered) {
    return false
  }

  return (
    (profile.rejectionDefects?.length ?? 0) > 0 ||
    (profile.advisoryDefects?.length ?? 0) > 0 ||
    profile.thisVehicle !== undefined
  )
}

/**
 * The line under the heading: what was actually measured.
 *
 * This is not decoration. The whole section is a statistic about other cars, and the sample it was
 * taken over is what tells a reader how much to trust it — a share over 1,420 inspections and one
 * over 186 look identical on screen and are not remotely the same claim. The API returns both
 * numbers for that reason, so they belong where the reader sees them first.
 */
export function defectProfileSubtitle(profile: VehicleDefectProfile): string {
  const vehicles = profile.vehiclesSampled
  const cohort = profile.vehiclesInCohort
  const inspections = profile.inspectionsCovered

  // The sample is capped, so on a common model it is a slice of a much larger population. Saying
  // "500 van de 13.861" is honest where "500 auto's" would imply the model is rare.
  const measured =
    vehicles !== undefined && cohort !== undefined && cohort > vehicles
      ? `${format(vehicles)} van de ${format(cohort)} auto's`
      : vehicles !== undefined
        ? `${format(vehicles)} ${vehicles === 1 ? 'auto' : "auto's"}`
        : undefined

  const parts = [
    measured && `Gemeten aan ${measured} ${describeCohort(profile)}`,
    inspections !== undefined &&
      `samen ${format(inspections)} ${inspections === 1 ? 'APK-keuring' : 'APK-keuringen'}`,
  ].filter((part): part is string => typeof part === 'string')

  return parts.join(', ')
}

/**
 * The model the figures were measured over, as a reader names it: "BMW 3-Serie".
 *
 * Only a catalogue answer carries one. There the cohort is approval + brand + model, which is a
 * different and narrower group than "same type approval" (a Clio and a Captur share an approval and
 * are measured apart), so the old phrase would be wrong for it. The trim on the plate ("320E") is
 * not the cohort, which is why this is a separate name and not the report's own heading.
 */
export function defectProfileCohortName(profile: VehicleDefectProfile): string | undefined {
  return profile.cohortModel ? vehicleName(profile.cohortBrand, profile.cohortModel) : undefined
}

/**
 * Who "other cars" are, as the tail of a sentence.
 *
 * The model is named rather than pluralised: "BMW 3-Serie's" happens to read well, "Golf's" and
 * "Up!'s" do not, and the catalogue holds thousands of names nobody will check one by one.
 */
function describeCohort(profile: VehicleDefectProfile): string {
  const name = defectProfileCohortName(profile)
  return name ? `van het model ${name}` : 'met dezelfde typegoedkeuring'
}

/**
 * When the figures were counted, as the closing line of the card, or `undefined` on a live answer.
 *
 * A catalogue answer is a snapshot rebuilt once a month, so on the day before a rebuild it is weeks
 * old. That is fine for running totals over years of inspections, but the page must not let it pass
 * for a count made just now, which is what every other figure in the report is.
 */
export function defectProfileDataNote(profile: VehicleDefectProfile): string | undefined {
  const date = formatLongDate(profile.dataAsOf)
  return date && `Cijfers van ${date}, maandelijks bijgewerkt.`
}

/**
 * The footnote's opening clause: whose cars these are.
 *
 * Kept beside the subtitle's wording so the two cannot describe the cohort differently.
 */
export function defectProfileScope(profile: VehicleDefectProfile): string {
  return `Deze cijfers gaan over andere auto's ${describeCohort(profile)}, niet over deze auto.`
}

/** One entry, ready to render. */
export interface DefectRow {
  label: string
  code: string
  occurrences: number
  sharePercent: number
}

/**
 * The head of one ranked list.
 *
 * The tail is dropped rather than summed, unlike the colour histogram in `rarity.ts`: colours
 * partition a population and have to add up, where a defect code is one of two hundred independent
 * things that can be noted at an inspection. An "overige gebreken" row would be a total nobody could
 * interpret.
 *
 * A code whose description the RDW no longer publishes keeps its number. That is thin, but it is
 * true, and the alternative is dropping a real finding because its label is missing.
 */
export function summariseDefects(defects: DefectFrequency[] | undefined, limit = DEFAULT_LIMIT): DefectRow[] {
  return (defects ?? []).slice(0, limit).map((defect) => ({
    label: defect.description ?? `Gebrekcode ${defect.code}`,
    code: defect.code,
    occurrences: defect.occurrences,
    sharePercent: defect.shareOfInspectionsPercent,
  }))
}

function format(value: number): string {
  return new Intl.NumberFormat('nl-NL').format(value)
}
